// src/components/resources/guides/GuidesContent.tsx

'use client'

import { useState } from 'react'
import { GuideCard } from '~/components/resources/guides/GuideCard'
import { GuideFilters } from '~/components/resources/guides/GuideFilters'
import type { BaseGuide } from '~/lib/resources/types/guides'

interface GuidesContentProps {
  guides: BaseGuide[]
  /**
   * Per-guide "Updated on" date floored by the sitemap/JSON-LD resolver, keyed by
   * guide id (computed server-side in the route). Keyed rather than attached to
   * the guide object so it survives the client-side filtering below.
   */
  displayLastUpdated?: Record<string, string>
}

export function GuidesContent({
  guides,
  displayLastUpdated,
}: GuidesContentProps): JSX.Element {
  const [filteredGuides, setFilteredGuides] = useState(guides)
  const featuredGuides = filteredGuides.filter(g => g.featured)
  const regularGuides = filteredGuides.filter(g => !g.featured)

  return (
    <>
      {/* Filters */}
      <GuideFilters guides={guides} onFilter={setFilteredGuides} />

      {/* Featured Guides */}
      {featuredGuides.length > 0 && (
        <section className="mb-16">
          <h2 className="sr-only">Featured Guides</h2>
          <div className="grid gap-8 lg:grid-cols-2">
            {featuredGuides.map(guide => (
              <GuideCard
                key={guide.id}
                guide={guide}
                featured
                displayLastUpdated={displayLastUpdated?.[guide.id]}
              />
            ))}
          </div>
        </section>
      )}

      {/* All Guides */}
      <section>
        <h2 className="sr-only">All Guides</h2>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {regularGuides.map(guide => (
            <GuideCard
              key={guide.id}
              guide={guide}
              displayLastUpdated={displayLastUpdated?.[guide.id]}
            />
          ))}
        </div>

        {/* Empty State */}
        {regularGuides.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center dark:border-gray-800">
            <div className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-600">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No guides found</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Try adjusting your filters or search query.
            </p>
          </div>
        )}
      </section>
    </>
  )
} 